import React from 'react';
import { skipToken, useQuery } from '@tanstack/react-query';
import { Button, Drawer, DrawerTrigger, DrawerCloseButton, Text, Flex, TagBadge } from '@chatsdet/ui';
import {
  CaretRight,
  CheckCircle,
  XCircle,
  MinusCircle,
  Stethoscope,
  Sparkle,
  WarningCircle,
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import type { ApiSequenceStepResultDto } from '../pages/FlowDesigner/hooks/useWorkflowRun';
import { JsonBlock } from './JsonBlock';
import { ApiSequenceDoctorView, type DoctorState } from './ApiSequenceDoctorView';
import { API_URL } from '../../config';
import { toApiError } from '../../lib/api-error';

type ApiSequenceResultDrawerProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  stepResults: ApiSequenceStepResultDto[];
  /** Index of the call to auto-expand (e.g. the first failed call) */
  focusIndex?: number;
  /** Inline doctor state — when provided, enables the doctor button and inline view */
  doctor?: DoctorState;
  fullBodySource?: { workflowId: string; runId: string; runStepId: string };
};

type FullApiCallBodyResponse = {
  ok: true;
  data: { stepId: string; body: string | null; requestBody: string | null };
};

function isTruncatedBody(body: string | null | undefined): boolean {
  // Mirrors apps/api/src/workflows/routes/shared.ts: 500 characters plus an appended '…'.
  return Boolean(body && body.length > 500 && body.endsWith('…'));
}

const methodColors: Record<string, string> = {
  GET: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  POST: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  PUT: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  PATCH: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  DELETE: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

function statusCodeColor(code: number | undefined): string {
  if (!code) return 'text-muted-foreground';
  if (code >= 200 && code < 300) return 'text-green-600 dark:text-green-400';
  if (code >= 400) return 'text-red-600 dark:text-red-400';
  return 'text-amber-600 dark:text-amber-400';
}

function HeadersList({ headers }: { headers: Record<string, string> }) {
  const entries = Object.entries(headers);
  const [expanded, setExpanded] = React.useState(false);

  if (entries.length === 0) return null;

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors py-0.5"
      >
        <motion.div animate={{ rotate: expanded ? 90 : 0 }} transition={{ duration: 0.15 }}>
          <CaretRight size={10} />
        </motion.div>
        <span>Headers ({entries.length})</span>
      </button>
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="ml-3 mt-1 space-y-0.5">
              {entries.map(([key, value]) => (
                <div key={key} className="flex gap-1.5 text-xs font-mono">
                  <span className="text-muted-foreground shrink-0">{key}:</span>
                  <span className="text-foreground break-all">{value}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function ApiCallDetail({
  result,
  fullBodySource,
  isActive,
}: {
  result: ApiSequenceStepResultDto;
  fullBodySource?: { workflowId: string; runId: string; runStepId: string };
  isActive: boolean;
}) {
  const shouldFetchFullBody = isTruncatedBody(result.body) || isTruncatedBody(result.requestBody);
  const fullBodyQuery = useQuery({
    queryKey: [
      'workflow-run-api-call-body',
      fullBodySource?.workflowId,
      fullBodySource?.runId,
      fullBodySource?.runStepId,
      result.stepId,
    ],
    enabled: Boolean(isActive && shouldFetchFullBody && fullBodySource),
    queryFn: fullBodySource
      ? async () => {
          const response = await fetch(
            `${API_URL}/workflows/${encodeURIComponent(fullBodySource.workflowId)}/runs/${encodeURIComponent(fullBodySource.runId)}/steps/${encodeURIComponent(fullBodySource.runStepId)}/api-calls/${encodeURIComponent(result.stepId)}`,
            { credentials: 'include' }
          );
          if (!response.ok) throw await toApiError(response, 'Failed to load full API call body');
          const payload = (await response.json()) as FullApiCallBodyResponse;
          if (!payload.ok) throw new Error('Failed to load full API call body');
          return payload.data;
        }
      : skipToken,
  });
  const requestBody = fullBodyQuery.data?.requestBody ?? result.requestBody;
  const responseBody = fullBodyQuery.data?.body ?? result.body;
  const method = (result.method ?? 'GET').toUpperCase();
  const methodClass = methodColors[method] ?? 'bg-muted text-muted-foreground';
  const hasRequestDetail = result.resolvedUrl || result.requestHeaders || result.requestBody;
  const hasResponseDetail = result.headers || result.body;

  return (
    <div className="space-y-3 pl-4 border-l-2 border-border/50 ml-2">
      {/* Error message */}
      {result.error && (
        <div className="flex items-start gap-1.5 rounded-md bg-destructive/10 border border-destructive/30 px-2.5 py-1.5">
          <WarningCircle size={14} weight="fill" className="text-destructive shrink-0 mt-0.5" />
          <Text size="xs" className="text-destructive">
            {result.error}
          </Text>
        </div>
      )}

      {/* Validation results */}
      {result.validationResults && result.validationResults.length > 0 && (
        <div className="space-y-1">
          <Text
            size="xs"
            weight="medium"
            className="text-muted-foreground uppercase tracking-wider"
          >
            Validations
          </Text>
          {result.validationResults.map((vr, i) => (
            <div key={i} className="flex items-start gap-1.5">
              {vr.passed ? (
                <CheckCircle size={13} weight="fill" className="text-green-500 shrink-0 mt-0.5" />
              ) : (
                <XCircle size={13} weight="fill" className="text-red-500 shrink-0 mt-0.5" />
              )}
              <Text
                size="xs"
                className={`font-mono ${vr.passed ? 'text-muted-foreground' : 'text-destructive'}`}
              >
                {vr.message}
              </Text>
            </div>
          ))}
        </div>
      )}

      {/* Request section */}
      {hasRequestDetail && (
        <div className="space-y-1.5">
          <Text
            size="xs"
            weight="medium"
            className="text-muted-foreground uppercase tracking-wider"
          >
            Request
          </Text>
          {result.resolvedUrl && (
            <div className="flex items-center gap-1.5">
              <span
                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${methodClass} shrink-0 font-mono`}
              >
                {method}
              </span>
              <Text size="xs" className="font-mono break-all text-foreground">
                {result.resolvedUrl}
              </Text>
            </div>
          )}
          {result.requestHeaders && Object.keys(result.requestHeaders).length > 0 && (
            <HeadersList headers={result.requestHeaders} />
          )}
          {requestBody && <JsonBlock content={requestBody} maxHeight="12rem" />}
        </div>
      )}

      {/* Response section */}
      {hasResponseDetail && (
        <div className="space-y-1.5">
          <Flex align="center" gap="xs">
            <Text
              size="xs"
              weight="medium"
              className="text-muted-foreground uppercase tracking-wider"
            >
              Response
            </Text>
            {result.statusCode !== null && result.statusCode !== undefined && (
              <Text
                size="xs"
                className={`font-mono font-semibold ${statusCodeColor(result.statusCode)}`}
              >
                {result.statusCode} {result.statusText}
              </Text>
            )}
          </Flex>
          {result.headers && Object.keys(result.headers).length > 0 && (
            <HeadersList headers={result.headers} />
          )}
          {responseBody && <JsonBlock content={responseBody} maxHeight="16rem" />}
        </div>
      )}

      {/* No detail available (old run data) */}
      {!hasRequestDetail && !hasResponseDetail && !result.error && (
        <Text size="xs" className="text-muted-foreground italic">
          No request/response detail available for this call.
        </Text>
      )}
      {isActive && shouldFetchFullBody && fullBodyQuery.isLoading && (
        <Text size="xs" className="text-muted-foreground">
          Loading full API call body…
        </Text>
      )}
      {isActive && shouldFetchFullBody && fullBodyQuery.isError && (
        <div className="flex items-start gap-1.5 rounded-md bg-destructive/10 border border-destructive/30 px-2.5 py-1.5">
          <WarningCircle size={14} weight="fill" className="text-destructive shrink-0 mt-0.5" />
          <Text size="xs" className="text-destructive">
            {fullBodyQuery.error.message}
          </Text>
        </div>
      )}
    </div>
  );
}

function ApiCallRow({
  result,
  index: _index,
  defaultExpanded,
  fullBodySource,
  isDrawerOpen,
}: {
  result: ApiSequenceStepResultDto;
  index: number;
  defaultExpanded: boolean;
  fullBodySource?: { workflowId: string; runId: string; runStepId: string };
  isDrawerOpen: boolean;
}) {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const method = (result.method ?? 'GET').toUpperCase();
  const methodClass = methodColors[method] ?? 'bg-muted text-muted-foreground';
  const isSkipped =
    (result.statusCode === null || result.statusCode === undefined) && !result.error;

  return (
    <div className={`${isSkipped ? 'opacity-50' : ''}`}>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 py-2 px-2 rounded-md hover:bg-muted/50 transition-colors text-left"
      >
        {/* Status icon */}
        {result.success ? (
          <CheckCircle size={16} weight="fill" className="text-green-500 shrink-0" />
        ) : isSkipped ? (
          <MinusCircle size={16} weight="fill" className="text-muted-foreground shrink-0" />
        ) : (
          <XCircle size={16} weight="fill" className="text-red-500 shrink-0" />
        )}

        {/* Method badge */}
        <span
          className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${methodClass} shrink-0 font-mono`}
        >
          {method}
        </span>

        {/* URL */}
        <Text size="xs" className="text-foreground truncate min-w-0 font-mono flex-1">
          {result.resolvedUrl ? result.resolvedUrl.replace(/^https?:\/\//, '') : result.stepId}
        </Text>

        {/* Status code */}
        {result.statusCode !== null && result.statusCode !== undefined && (
          <Text size="xs" className={`${statusCodeColor(result.statusCode)} font-mono shrink-0`}>
            {result.statusCode}
          </Text>
        )}

        {/* Skipped label */}
        {isSkipped && (
          <Text size="xs" className="text-muted-foreground shrink-0 italic">
            (skipped)
          </Text>
        )}

        {/* Duration */}
        {result.durationMs !== null && result.durationMs !== undefined && (
          <Text
            size="xs"
            className="text-muted-foreground w-14 text-right shrink-0 font-mono tabular-nums"
          >
            {result.durationMs}ms
          </Text>
        )}

        {/* Expand caret */}
        <motion.div
          animate={{ rotate: expanded ? 90 : 0 }}
          transition={{ duration: 0.15 }}
          className="shrink-0"
        >
          <CaretRight size={12} className="text-muted-foreground" />
        </motion.div>
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="pb-2 px-1">
              <ApiCallDetail
                result={result}
                fullBodySource={fullBodySource}
                isActive={expanded && isDrawerOpen}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export const ApiSequenceResultDrawer: React.FC<ApiSequenceResultDrawerProps> = ({
  isOpen,
  onOpenChange,
  stepResults,
  focusIndex,
  doctor,
  fullBodySource,
}) => {
  const failedCount = stepResults.filter(
    (r) => !r.success && r.statusCode !== null && r.statusCode !== undefined
  ).length;
  const totalDurationMs = stepResults.reduce((sum, r) => sum + (r.durationMs ?? 0), 0);

  // Auto-expand the focused call, or the first failed call
  const autoExpandIndex = focusIndex ?? stepResults.findIndex((r) => !r.success);

  return (
    <DrawerTrigger isOpen={isOpen} onOpenChange={onOpenChange} className="max-w-[560px]">
      <span style={{ display: 'none' }} />
      <Drawer hideCloseButton>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div>
              <Text size="lg" weight="semibold">
                API Sequence Results
              </Text>
              <Flex align="center" gap="xs" className="mt-0.5">
                <Text size="xs" className="text-muted-foreground">
                  {stepResults.length} call{stepResults.length !== 1 ? 's' : ''}
                </Text>
                {failedCount > 0 && (
                  <>
                    <Text size="xs" className="text-muted-foreground">
                      &middot;
                    </Text>
                    <TagBadge name={<>{failedCount} failed</>} color="orange" hideDot size="sm" />
                  </>
                )}
                {doctor && doctor.status === 'idle' && (
                  <Button variant="secondary" size="sm" onPress={doctor.onRunDoctor}>
                    {doctor.mode === 'improve' ? <Sparkle size={14} /> : <Stethoscope size={14} />}
                    {doctor.mode === 'improve' ? 'Improve' : 'Fix'}
                  </Button>
                )}
                <Text size="xs" className="text-muted-foreground">
                  &middot;
                </Text>
                <Text size="xs" className="text-muted-foreground font-mono tabular-nums">
                  {totalDurationMs}ms
                </Text>
              </Flex>
            </div>
            <DrawerCloseButton />
          </div>

          {/* Doctor view (when active) or call list */}
          {doctor && doctor.status !== 'idle' ? (
            <ApiSequenceDoctorView doctor={doctor} />
          ) : (
            <div className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
              {stepResults.map((result, idx) => (
                <ApiCallRow
                  key={result.stepId}
                  result={result}
                  index={idx}
                  defaultExpanded={idx === autoExpandIndex}
                  fullBodySource={fullBodySource}
                  isDrawerOpen={isOpen}
                />
              ))}
            </div>
          )}
        </div>
      </Drawer>
    </DrawerTrigger>
  );
};
